import { Tables } from "database.types";
import { CardWithRelations } from "@/hooks/useCards";
import { Plus } from "lucide-react";
import { Button } from "@/components/ui/button";
import { AddCardToBinderDialog } from "@/components/binder/add/AddCardToBinderDialog/AddCardToBinderDialog";
import { useCallback, useMemo, useState } from "react";
import { CardVariantWithBinder } from "@/types/varinats";
import { BinderVariant } from "@/types/binders";
import { SelectBinderVariantsModal } from "@/components/binder/edit/SelectBinderVariantsModal";
import { EditCardInBinderDialog } from "@/components/binder/add/EditCardInBinderDialog/EditCardInBinderDialog";

export interface Props {
  card: CardWithRelations;
  variant: CardVariantWithBinder;
}

export function CardVariantItemBinders({ card, variant }: Props) {
  const [isOpenAddToBinderDialog, setIsOpenAddToBinderDialog] = useState(false);

  const [selectedBinder, setSelectedBinder] =
    useState<Tables<"binders"> | null>(null);

  const [selectedBinderVariant, setSelectedBinderVariant] =
    useState<BinderVariant | null>(null);

  const [isOpenEditBinderVariantDialog, setIsOpenEditBinderVariantDialog] =
    useState(false);

  const onSelectBinderVariant = useCallback((binderVariant: BinderVariant) => {
    setSelectedBinderVariant(binderVariant);
    setIsOpenEditBinderVariantDialog(true);
  }, []);

  const [isOpenSelectBinderVariantsModal, setIsOpenSelectBinderVariantsModal] =
    useState(false);

  const onSelectBinderItem = useCallback((binder: Tables<"binders">) => {
    setSelectedBinder(binder);
    setIsOpenSelectBinderVariantsModal(true);
  }, []);

  const bindersWithVariants = useMemo(() => {
    const bindersToVariants = new Map<
      Tables<"binders">["id"],
      BinderVariant[]
    >();

    variant.binders?.forEach((binder) => {
      const variantBinder = {
        ...binder.vtb,
        grade: binder.grade,
        graded_card: binder.graded_card,
        grading_company: binder.grading_company,
        variant: {
          ...variant,
          card: card,
          condition: variant.condition,
        },
      } satisfies BinderVariant;

      const arr = bindersToVariants.get(binder.vtb.binder_id) || [];

      arr.push(variantBinder);
      bindersToVariants.set(binder.vtb.binder_id, arr);
    });

    return [...bindersToVariants.entries()].map(([binderId, binderVariant]) => {
      const binder = variant.binders?.find((b) => b.vtb.binder_id === binderId);

      if (!binder) {
        throw new Error("Expected binder to exist");
      }

      return {
        binder: binder.binder,
        binderVariant,
      };
    });
  }, [card, variant]);

  const selectedBinderItems = useMemo(() => {
    return bindersWithVariants
      .filter(({ binder }) => binder.id === selectedBinder?.id)
      .flatMap(({ binderVariant }) => binderVariant);
  }, [bindersWithVariants, selectedBinder]);

  return (
    <>
      {bindersWithVariants.map(({ binder, binderVariant }) => {
        return (
          <Button
            variant="outline"
            key={binder.id}
            size="sm"
            onClick={() => onSelectBinderItem(binder)}
          >
            {binder.name} ({binderVariant.length})
          </Button>
        );
      })}
      <Button size="sm" onClick={() => setIsOpenAddToBinderDialog(true)}>
        <Plus className="w-4 h-4" />
        Add to Binder
      </Button>
      <AddCardToBinderDialog
        cardId={card.id}
        open={isOpenAddToBinderDialog}
        onOpenChange={setIsOpenAddToBinderDialog}
        variantId={variant.id}
      />
      {selectedBinderItems.length > 0 && (
        <SelectBinderVariantsModal
          open={isOpenSelectBinderVariantsModal}
          onOpenChange={setIsOpenSelectBinderVariantsModal}
          binderItems={selectedBinderItems}
          onSelectBinderItem={onSelectBinderVariant}
        />
      )}
      {selectedBinderVariant && (
        <EditCardInBinderDialog
          binderItem={selectedBinderVariant}
          open={isOpenEditBinderVariantDialog}
          onOpenChange={setIsOpenEditBinderVariantDialog}
        />
      )}
    </>
  );
}
